import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteInfo } from '@/mocks/site';
import { articles } from '@/mocks/articles';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9sra9llbrt1qcgaj8mg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v as string])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查您的连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: `${siteInfo.url}/about`,
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteInfo.url}/about` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、使命愿景与核心团队。${siteInfo.description}`}
        keywords={`关于我们,品牌介绍,${siteInfo.keywords}`}
        canonicalUrl={`${siteInfo.url}/about`}
        ogType="website"
      />

      <main>
        <Navbar />

        {/* Header */}
        <section className="pt-24 pb-12 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mt-4 mb-3">关于我们</h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
              {siteInfo.tagline}
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">品牌故事</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  {siteInfo.name}成立于{siteInfo.foundedYear}年，源于创始团队对当代婚姻关系的深度观察。我们发现在社交环境日益复杂的今天，婚姻中的社交边界问题越来越突出，但却鲜有平台系统性地探讨这个话题。
                </p>
                <p>
                  我们的创始团队包括婚姻家庭咨询师、心理学研究者以及多位在婚姻生活中积累了丰富经验的内容创作者。我们相信，每一段婚姻都值得被认真经营和守护。
                </p>
                <p>
                  自成立以来，我们已经发布了<strong>{siteInfo.stats[0].value}</strong>篇原创文章，服务了超过<strong>{siteInfo.stats[1].value}</strong>位读者，完成了<strong>{siteInfo.stats[2].value}</strong>次婚姻咨询服务，读者满意度达到<strong>{siteInfo.stats[3].value}</strong>。
                </p>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20minimalist%20illustration%20of%20a%20cozy%20living%20room%20with%20a%20couple%20reading%20books%20together%20on%20a%20comfortable%20sofa%2C%20soft%20afternoon%20sunlight%20through%20large%20windows%2C%20gentle%20beige%20and%20cream%20tones%2C%20editorial%20style%2C%20peaceful%20atmosphere&width=800&height=600&seq=about-brand-story&orientation=landscape"
                alt="品牌故事"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">我们的使命</h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                帮助每一位已婚人士建立健康的社交边界，守护珍贵的婚姻关系
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 mx-auto mb-4">
                  <i className="ri-lightbulb-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">知识普及</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  用专业、易懂的内容，帮助大众理解婚姻社交边界的重要性
                </p>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 mx-auto mb-4">
                  <i className="ri-heart-pulse-line text-xl text-accent-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">关系守护</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  提供实用的工具和方法，帮助夫妻共同建立和维护健康的社交边界
                </p>
              </div>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-100 mx-auto mb-4">
                  <i className="ri-group-2-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">社区共建</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  打造一个开放、安全的交流社区，让读者在分享中成长、在共鸣中前行
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">读者心声</h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              来自真实读者的反馈，这是我们不断前行的动力
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {siteInfo.testimonials.map((t, i) => (
              <div key={i} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(t.rating)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "{t.content}"
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary-700">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.age}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Latest Articles */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">最新文章</h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                持续更新的优质内容，帮助你不断提升婚姻经营能力
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {articles.slice(0, 4).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-background-300/60 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-700 mb-2 whitespace-nowrap">
                      {article.category}
                    </span>
                    <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">联系我们</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                如果你有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们重视每一位读者的声音。
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-mail-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                    <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                      {siteInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                    <i className="ri-wechat-line text-accent-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">微信公众号</p>
                    <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
                    <i className="ri-phone-line text-secondary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">客服电话</p>
                    <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-500"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">办公地址</p>
                    <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">留言给我们</h2>
              <form onSubmit={handleSubmit} className="bg-background-50 rounded-lg border border-background-200/70 p-5 md:p-6" data-readdy-form="true">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容 <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                  </div>
                  <div className="absolute opacity-0 pointer-events-none" style={{ position: 'absolute', left: '-9999px' }}>
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    提交留言
                  </button>
                </div>

                {formStatus === 'success' && (
                  <div className="mt-4 p-3 rounded-md bg-accent-100 text-accent-700 text-sm">
                    感谢您的留言！我们会尽快与您取得联系。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mt-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        <Footer />
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}